'use client';
import React, { useEffect } from 'react';
import { CheckCircle, Clock, AlertTriangle, XCircle, Star, Layers } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';
import { usePlacementCatalog } from '@/hooks/usePlacementCatalog';

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

function AvailBadge({ capacity, filled }: { capacity: number; filled: number }) {
  const rem = capacity - filled;
  if (rem === 0) return <span className="availability-sold-out chip text-xs"><XCircle size={10} className="mr-1" />Sold Out</span>;
  if (rem === 1 && capacity === 1) return <span className="availability-available chip text-xs"><CheckCircle size={10} className="mr-1" />Available</span>;
  if (rem <= 2) return <span className="availability-limited chip text-xs"><AlertTriangle size={10} className="mr-1" />{rem} left</span>;
  return <span className="availability-available chip text-xs"><CheckCircle size={10} className="mr-1" />{rem} slots</span>;
}

export default function StepC({ order, updateOrder, showErrors }: Props) {
  const { placements } = usePlacementCatalog(order.issue || undefined);
  const selectedPlacement = placements.find(p => p.id === order.placement);
  const missingPlacement = showErrors && !order.placement;

  const bookPlacements = placements.filter(p => !p.isFlyerInsert);
  const insertPlacements = placements.filter(p => p.isFlyerInsert);

  useEffect(() => {
    if (!selectedPlacement) return;
    const livePrice = order.term === 'one' ? selectedPlacement.oneIssue : selectedPlacement.threeIssue;
    if (order.placementBasePrice !== livePrice || order.placementName !== selectedPlacement.name) {
      updateOrder({
        placementBasePrice: livePrice,
        placementName: selectedPlacement.name,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlacement, order.term, order.placement]);

  const renderPlacementCard = (p: typeof placements[0]) => {
    const price = order.term === 'one' ? p.oneIssue : p.threeIssue;
    const isSoldOut = p.defaultFilled >= p.capacity;
    const isSelected = order.placement === p.id;

    return (
      <button
        key={`placement-step-${p.id}`}
        onClick={() => !isSoldOut && updateOrder({
          placement: p.id,
          placementName: p.name,
          placementBasePrice: order.term === 'one' ? p.oneIssue : p.threeIssue,
        })}
        disabled={isSoldOut}
        className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
          isSoldOut
            ? 'border-white/5 bg-navy-800/40 opacity-50 cursor-not-allowed'
            : isSelected
            ? 'border-brand-500/60 bg-brand-600/12 shadow-glow-sm'
            : missingPlacement
            ? 'border-red-500/40 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3' :'border-white/10 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
              isSelected ? 'bg-gradient-primary shadow-glow-sm' : 'bg-navy-600 border border-white/10'
            }`}>
              {isSelected
                ? <CheckCircle size={16} className="text-white" />
                : p.isFlyerInsert
                ? <Layers size={14} className="text-slate-400" />
                : <span className="text-xs font-bold text-slate-400">{p.id === 'standard' ? '9' : '1'}</span>
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-white">{p.name}</span>
                {p.premium && <span className="chip text-xs" style={{ fontSize: '10px' }}>Premium</span>}
                {p.isFlyerInsert && (
                  <span className="chip text-xs bg-amber-500/15 border-amber-500/25 text-amber-400" style={{ fontSize: '10px' }}>
                    Insert
                  </span>
                )}
                {p.premium && !isSoldOut && !p.isFlyerInsert && (
                  <span className="flex items-center gap-1 text-xs text-amber-400">
                    <Clock size={10} />15-min hold
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{p.position}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-3">
            <AvailBadge capacity={p.capacity} filled={p.defaultFilled} />
            <div className="text-right">
              <p className="font-display font-bold text-base text-white tabular-nums">
                ${price.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">
                {order.term === 'three' ? '/mo per region' : 'per region'}
              </p>
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Placement & Term</h2>
        <p className="text-sm text-slate-400">Choose your ad position and booking term. Premium placements are exclusive — 1 per region.</p>
      </div>

      {/* Term toggle */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-slate-300 mb-3">Ad Term</label>
        <div className="flex p-1 glass-card rounded-xl w-fit">
          <button
            onClick={() => {
              const newTerm = 'one';
              const updates: Partial<OrderState> = { term: newTerm };
              if (selectedPlacement) {
                updates.placementBasePrice = selectedPlacement.oneIssue;
              }
              updateOrder(updates);
            }}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${order.term === 'one' ? 'bg-gradient-primary text-white shadow-glow-sm' : 'text-slate-400 hover:text-white'}`}
          >
            One Issue
          </button>
          <button
            onClick={() => {
              const newTerm = 'three';
              const updates: Partial<OrderState> = { term: newTerm };
              if (selectedPlacement) {
                updates.placementBasePrice = selectedPlacement.threeIssue;
              }
              updateOrder(updates);
            }}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${order.term === 'three' ? 'bg-gradient-primary text-white shadow-glow-sm' : 'text-slate-400 hover:text-white'}`}
          >
            <Star size={13} />
            3-Issue Monthly
          </button>
        </div>
        {order.term === 'three' && (
          <p className="text-xs text-green-400 mt-2 font-medium">Billed monthly for 3 consecutive issues. Cancel before issue 2 to avoid subsequent charges.</p>
        )}
      </div>

      {/* Book Placements */}
      <label className="block text-sm font-semibold text-slate-300 mb-3">
        Ad Placement <span className="text-red-400">*</span>
      </label>
      <div className="space-y-3">
        {bookPlacements.map(renderPlacementCard)}
      </div>

      {/* Flyer Insert section */}
      {insertPlacements.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-white/8" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
              <Layers size={12} className="text-amber-400" />
              <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Flyer Inserts</span>
            </div>
            <div className="flex-1 h-px bg-white/8" />
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Physical flyers inserted into every book. Available even when the book is sold out. Does not consume a book page.
          </p>
          <div className="space-y-3">
            {insertPlacements.map(renderPlacementCard)}
          </div>
        </div>
      )}

      {missingPlacement && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
          <AlertTriangle size={12} />
          Please select an ad placement.
        </p>
      )}
    </div>
  );
}